import type { SendingDisabledReason } from '@/services/db/delivery/sending-disabled-reason';
import { useSupportChat } from '../chat/hooks';
import type { SupportFormDiagnostics } from './types';

export interface SendingDomainSupport {
  /** `null` when the disabled domain is unknown: the form still opens, with no domain attached. */
  askAboutDisabledDomain: (domain: string | null) => void;
  /** Opens the form a delivery's compliance refusal calls for: its domain, or the workspace's email. */
  askAboutSendingDisabled: (reason: SendingDisabledReason) => void;
  requestIpPoolChange: (domain: string, requestedPoolTitle: string) => void;
}

function domainDiagnostics(lines: string[]): SupportFormDiagnostics {
  return { label: 'Sending domain', summary: lines.join('\n') };
}

/** Opens the support form for a sending domain, with the domain attached to the ticket. */
export function useSendingDomainSupport(): SendingDomainSupport {
  const { openForm } = useSupportChat();

  const askAboutDisabledDomain = (domain: string | null) => {
    openForm(undefined, 'sending_domain_disabled', {
      kind: 'sending_domain_disabled',
      diagnostics: domain == null ? undefined : domainDiagnostics([`Domain: ${domain}`]),
    });
  };

  return {
    askAboutDisabledDomain,
    askAboutSendingDisabled: (reason) => {
      if (reason.kind === 'domain') {
        askAboutDisabledDomain(reason.domain);
        return;
      }
      openForm(undefined, 'email_channel_disabled', { kind: 'email_channel_disabled' });
    },
    requestIpPoolChange: (domain, requestedPoolTitle) => {
      openForm(undefined, 'ip_pool_request_pending', {
        kind: 'ip_pool_request_change',
        diagnostics: domainDiagnostics([`Domain: ${domain}`, `Requested IP pool: ${requestedPoolTitle}`]),
      });
    },
  };
}
